"use client";

import { motion } from "framer-motion";

const trainingRoadmap = [
  {
    title: "Weeks 8-10 · Foundation",
    intention: "Build trust and introduce gentle handling.",
    focus: [
      "Pair every handling moment with calm voice and soft treats.",
      "Expose to three new scents and textures each day for sensory confidence.",
      "Begin name recognition using short, joyful call-and-reward games.",
    ],
  },
  {
    title: "Weeks 11-14 · Social fluency",
    intention: "Encourage curiosity and resilience.",
    focus: [
      "Rotate visitors (adults, child, supervised calm dog) with structured introductions.",
      "Practice travel conditioning with short carrier sessions and car rides.",
      "Introduce grooming tools—nail clipper, slicker brush, toothbrush—with rewards.",
    ],
  },
  {
    title: "Weeks 15-20 · Skill polishing",
    intention: "Refine cues your Balinese will use for life.",
    focus: [
      "Reinforce recall, station (place), and carrier cue daily.",
      "Expand leash or stroller training in quiet spaces.",
      "Begin gentle cooperative care drills: open mouth, touch paws, ear check.",
    ],
  },
  {
    title: "Beyond 20 weeks · Lifestyle integration",
    intention: "Sustain enrichment that evolves with maturity.",
    focus: [
      "Refresh skill sessions three times weekly—short, positive, and varied.",
      "Alternate high-energy play with scent games and puzzle feeders.",
      "Document behaviour notes to anticipate seasonal or developmental shifts.",
    ],
  },
];

const ritualStudios = [
  {
    icon: "🌅",
    title: "Morning calibration",
    summary: "Set tone for a balanced day in 15 minutes.",
    points: [
      "Sunrise greeting ritual with stretching and soft grooming.",
      "Short clicker session reinforcing name, sit, and target.",
      "Breakfast served in a puzzle bowl to awaken curiosity.",
    ],
  },
  {
    icon: "🎯",
    title: "Midday micro-training",
    summary: "Keep skills sharp with playful reps.",
    points: [
      "Rotate cues: spin, high-five, go-to-mat, recall.",
      "Layer in new textures—balance board, crinkle tunnel, tactile pads.",
      "Close with independent enrichment (snuffle mat, window perch).",
    ],
  },
  {
    icon: "🛁",
    title: "Grooming atelier",
    summary: "Transform maintenance into bonding.",
    points: [
      "Coat brushing + combing while rewarding stillness.",
      "Paw, ear, and dental checks using cooperative care positions.",
      "Hydrosol mist or calming wipe to finish with a signature scent.",
    ],
  },
  {
    icon: "🌙",
    title: "Evening decompression",
    summary: "Ease into night-time serenity.",
    points: [
      "Interactive play that mimics hunt-pounce-capture patterns.",
      "Dinner followed by a quiet chew or lick mat to release tension.",
      "Cue lights-down routine with lullaby playlist and cuddles on cue.",
    ],
  },
];

const skillLibrary = [
  {
    cue: "Recall",
    how: "Call name + upbeat whistle, reward when cat arrives at your feet.",
    goal: "Reliable response even with distractions.",
    frequency: "Daily 5 short reps",
  },
  {
    cue: "Station",
    how: "Lure onto mat, mark and reward calm settle for increasing duration.",
    goal: "Creates a relaxation anchor during guests, grooming, meals.",
    frequency: "3 sessions weekly",
  },
  {
    cue: "Carrier go-in",
    how: "Treat trail into carrier, feed inside, gradually close door briefly.",
    goal: "Stress-free travel and vet visits.",
    frequency: "2 sessions weekly",
  },
  {
    cue: "Touch target",
    how: "Present target stick, reward nose touch, move target to guide movement.",
    goal: "Foundation for agility, position changes, and photo posing.",
    frequency: "Every other day",
  },
  {
    cue: "Gentle paws",
    how: "Pair paw handling with verbal cue, reward relaxed muscles, release quickly.",
    goal: "Supports nail trims and vet handling.",
    frequency: "During grooming atelier",
  },
];

const troubleshootingDeck = [
  {
    title: "Reluctant to enter carrier",
    observations: [
      "Cat hesitates or vocalises near carrier door.",
      "Bolts away when carrier appears.",
    ],
    adjustments: [
      "Leave carrier open as a resting nook with bedding and treats inside.",
      "Feed one daily meal in the carrier using the door closed for 2-3 minutes.",
      "Practice calm exits—wait for stillness, then cue release word.",
    ],
  },
  {
    title: "Night-time energy spikes",
    observations: [
      "Zoomies increase after lights-out.",
      "Night meows seeking attention.",
    ],
    adjustments: [
      "Shift largest play session to 60 minutes before bedtime.",
      "Introduce final feeding right after play to satisfy hunt-eat-groom-sleep cycle.",
      "Offer overnight forage toy with a small portion of kibble to occupy instinct.",
    ],
  },
  {
    title: "Overstimulation during petting",
    observations: [
      "Tail flicks or nips after prolonged strokes.",
      "Dilated pupils while purring.",
    ],
    adjustments: [
      "Pet in brief intervals, pause before cat signals discomfort.",
      "Redirect energy into toy play; reward calm returns to lap.",
      "Teach a \"two-minute cuddle\" cue so expectations are clear.",
    ],
  },
];

const printablePrompts = [
  "Weekly training log to capture wins, challenges, and next micro-goal.",
  "Guest introduction checklist with scent exchange, visual introduction, and supervised session notes.",
  "Carrier conditioning tracker covering duration, door closure, and travel rehearsal milestones.",
  "Cue library sheet to record verbal cues, hand signals, and reinforcement types.",
  "Seasonal enrichment planner featuring rotation ideas for toys, scents, and climbing routes.",
];

export default function TrainingPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      <motion.section
        className="relative overflow-hidden px-6 pt-24 pb-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-soft-gold/20 via-cream/40 to-warm-brown/20" />
        <div className="relative z-10 mx-auto max-w-5xl text-center space-y-8">
          <div className="space-y-4">
            <p className="inline-block rounded-full bg-white/70 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-soft-gold">Training Atelier</p>
            <h1 className="font-heading text-4xl font-bold text-warm-brown sm:text-5xl md:text-6xl">Balinese Training Companion</h1>
            <p className="text-base text-warm-brown/75 sm:text-lg">
              Follow this complete roadmap to nurture poised, people-focused companions. Save or print so every family member coaches with the same calm voice.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#roadmap" className="rounded-xl bg-soft-gold px-6 py-3 text-sm font-semibold text-warm-brown transition-transform hover:-translate-y-0.5 hover:bg-soft-gold/90">Progression roadmap</a>
            <a href="#rituals" className="rounded-xl border border-soft-gold/50 px-6 py-3 text-sm font-semibold text-soft-gold transition-colors hover:bg-soft-gold/10">Daily rituals</a>
            <a href="#skills" className="rounded-xl border border-sand/40 px-6 py-3 text-sm font-semibold text-warm-brown/70 transition-colors hover:bg-sand/15">Core cues</a>
            <a href="#troubleshooting" className="rounded-xl border border-sand/40 px-6 py-3 text-sm font-semibold text-warm-brown/70 transition-colors hover:bg-sand/15">Troubleshooting</a>
            <a href="#resources" className="rounded-xl border border-sand/40 px-6 py-3 text-sm font-semibold text-warm-brown/70 transition-colors hover:bg-sand/15">Printable toolkit</a>
          </div>
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/60 bg-white/80 p-6 text-sm text-warm-brown/65 sm:text-base">
            <p>Print tip: press <strong>Ctrl/Cmd + P</strong>, choose “Save as PDF,” and slide this guide into your care binder. Update notes in pencil so your Balinese evolves with you.</p>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="roadmap"
        className="px-6 py-20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-10">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Maison Training Roadmap</h2>
            <p className="text-warm-brown/70 sm:text-lg">Move through each phase sequentially; linger as long as needed to keep progress joyful.</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {trainingRoadmap.map((stage) => (
              <div key={stage.title} className="rounded-3xl border border-sand/25 bg-white/85 p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-soft-gold">{stage.intention}</p>
                <h3 className="mt-3 font-heading text-xl font-semibold text-warm-brown">{stage.title}</h3>
                <ul className="mt-4 space-y-1.5 text-sm text-warm-brown/70">
                  {stage.focus.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        id="rituals"
        className="bg-white/50 px-6 py-20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-10">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Signature Daily Rituals</h2>
            <p className="text-warm-brown/70 sm:text-lg">Blend these studios across the day to sustain harmony and learning.</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {ritualStudios.map((studio) => (
              <div key={studio.title} className="rounded-3xl border border-sand/25 bg-white/80 p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <span className="text-3xl" aria-hidden>{studio.icon}</span>
                  <div className="space-y-2">
                    <h3 className="font-heading text-xl font-semibold text-warm-brown">{studio.title}</h3>
                    <p className="text-sm text-warm-brown/65">{studio.summary}</p>
                  </div>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-warm-brown/70">
                  {studio.points.map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        id="skills"
        className="px-6 py-20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-8">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Core Cue Library</h2>
            <p className="text-warm-brown/70 sm:text-lg">Track each cue’s method, intention, and ideal repetition schedule.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] divide-y divide-sand/30 rounded-2xl border border-sand/30 bg-white/80 text-sm">
              <thead className="bg-soft-gold/10 text-warm-brown/70">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-[0.2em] text-xs">Cue</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-[0.2em] text-xs">How to teach</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-[0.2em] text-xs">Why it matters</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-[0.2em] text-xs">Frequency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand/20 text-warm-brown/70">
                {skillLibrary.map((skill) => (
                  <tr key={skill.cue} className="hover:bg-soft-gold/5">
                    <td className="px-4 py-4 font-semibold text-warm-brown">{skill.cue}</td>
                    <td className="px-4 py-4">{skill.how}</td>
                    <td className="px-4 py-4">{skill.goal}</td>
                    <td className="px-4 py-4">{skill.frequency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-3xl border border-soft-gold/30 bg-soft-gold/10 p-6 text-sm text-warm-brown/70">
            <p><strong className="text-warm-brown">Coaching tip:</strong> keep sessions under three minutes. End while enthusiasm is high and log successes in your training journal.</p>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="troubleshooting"
        className="bg-white/50 px-6 py-20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-6xl space-y-10">
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Behaviour Troubleshooting Studio</h2>
            <p className="text-warm-brown/70 sm:text-lg">Observe, adjust, and celebrate progress without frustration.</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {troubleshootingDeck.map((card) => (
              <div key={card.title} className="rounded-3xl border border-sand/25 bg-white/85 p-6 shadow-sm">
                <h3 className="font-heading text-xl font-semibold text-warm-brown">{card.title}</h3>
                <div className="mt-4 space-y-2 text-sm text-warm-brown/70">
                  <p className="font-semibold text-warm-brown">Observe:</p>
                  <ul className="space-y-1.5">
                    {card.observations.map((obs) => (
                      <li key={obs} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                        <span>{obs}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="pt-2 font-semibold text-warm-brown">Adjust with:</p>
                  <ul className="space-y-1.5">
                    {card.adjustments.map((adj) => (
                      <li key={adj} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                        <span>{adj}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        id="resources"
        className="px-6 pb-24"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="mx-auto max-w-5xl space-y-6 text-center">
          <h2 className="font-heading text-3xl font-bold text-warm-brown sm:text-4xl">Printable Training Toolkit</h2>
          <p className="text-warm-brown/70 sm:text-lg">Capture your progress and keep every helper aligned with these checklists.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {printablePrompts.map((prompt) => (
              <div key={prompt} className="rounded-3xl border border-sand/25 bg-white/80 p-5 text-left text-sm text-warm-brown/70 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 flex-none rounded-full bg-soft-gold" />
                  <span>{prompt}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-warm-brown/60">Store printed pages in transparent sleeves and annotate with dry-erase markers so routines stay adaptive.</p>
        </div>
      </motion.section>
    </main>
  );
}
